/**
 * EntityPicker (P3-A) — single-select async combobox.
 *
 * Replaces text-input cho ID/code khắp builders.
 *
 * Doc 36 §4.1 + §5.
 */
import { Check, ChevronDown, Loader2, Search, X } from "lucide-react";
import * as React from "react";

import type { PickerItem, PickerKind } from "@/api/picker";
import { Input } from "@/components/ui/input";
import { usePickerOptions } from "@/hooks/usePickerOptions";
import { cn } from "@/lib/utils";

interface Props {
  kind: PickerKind;
  /** Selected value (id). */
  value: string | null | undefined;
  onChange: (id: string | null, item: PickerItem | null) => void;
  /** Cascading: vd `parentId={modelName}` cho `kind="odoo_field"`. */
  parentId?: string;
  filters?: Record<string, unknown>;
  placeholder?: string;
  /** Disable picker (vd cascading parent chưa chọn). */
  disabled?: boolean;
  /** Cho phép clear → null. */
  clearable?: boolean;
  /** Cho phép gõ tự do (vd ID không có trong list nhưng user muốn dùng). */
  allowFreeText?: boolean;
  className?: string;
  /** Hint hiển thị bên dưới khi chưa chọn. */
  hint?: string;
}

export function EntityPicker({
  kind,
  value,
  onChange,
  parentId,
  filters,
  placeholder = "Chọn...",
  disabled = false,
  clearable = true,
  allowFreeText = false,
  className,
  hint,
}: Props): JSX.Element {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState("");
  const [activeIdx, setActiveIdx] = React.useState(0);
  const [selectedItem, setSelectedItem] = React.useState<PickerItem | null>(null);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);

  const { items, loading, error } = usePickerOptions(kind, {
    q: query,
    parentId,
    filters,
    enabled: open,
  });

  // Resolve selected item label khi chưa có trong items list (initial load)
  React.useEffect(() => {
    if (!value) {
      setSelectedItem(null);
      return;
    }
    const found = items.find((it) => it.id === value);
    if (found) setSelectedItem(found);
  }, [value, items]);

  // Close on outside click
  React.useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent): void => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  // Reset active idx khi items thay đổi
  React.useEffect(() => {
    setActiveIdx(0);
  }, [items.length]);

  const pick = (item: PickerItem): void => {
    onChange(item.id, item);
    setSelectedItem(item);
    setOpen(false);
    setQuery("");
  };

  const clear = (e: React.MouseEvent): void => {
    e.stopPropagation();
    onChange(null, null);
    setSelectedItem(null);
    setQuery("");
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (!open) {
        setOpen(true);
        return;
      }
      setActiveIdx((i) => Math.min(i + 1, Math.max(items.length - 1, 0)));
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
      return;
    }
    if (e.key === "Enter") {
      e.preventDefault();
      const it = items[activeIdx];
      if (it) {
        pick(it);
      } else if (allowFreeText && query.trim()) {
        // Free text fallback
        const synth: PickerItem = { id: query.trim(), label: query.trim() };
        pick(synth);
      }
    }
  };

  const display =
    selectedItem?.label ?? (value ? value : null);

  return (
    <div className={cn("relative", className)} ref={containerRef}>
      <button
        type="button"
        onClick={() => {
          if (disabled) return;
          setOpen((v) => !v);
          setTimeout(() => inputRef.current?.focus(), 0);
        }}
        disabled={disabled}
        className={cn(
          "w-full h-8 flex items-center gap-2 px-2 rounded-md border bg-background text-sm",
          "hover:border-primary/50 transition-colors text-left",
          "disabled:opacity-50 disabled:cursor-not-allowed",
          open && "border-primary ring-1 ring-primary/30",
        )}
      >
        {display ? (
          <>
            <span className="truncate flex-1 min-w-0">{display}</span>
            {selectedItem?.secondary ? (
              <span className="text-[10px] text-muted-foreground truncate hidden sm:inline">
                {selectedItem.secondary}
              </span>
            ) : null}
            {clearable && !disabled ? (
              <span
                role="button"
                tabIndex={-1}
                onClick={clear}
                className="text-muted-foreground hover:text-destructive shrink-0"
                aria-label="Clear"
              >
                <X className="size-3.5" />
              </span>
            ) : null}
          </>
        ) : (
          <span className="text-muted-foreground flex-1 truncate">{placeholder}</span>
        )}
        <ChevronDown
          className={cn(
            "size-3.5 text-muted-foreground transition-transform shrink-0",
            open && "rotate-180",
          )}
        />
      </button>

      {open ? (
        <div className="absolute z-[55] mt-1 w-full max-h-72 overflow-y-auto rounded-md border bg-popover shadow-lg animate-fade-in">
          <div className="flex items-center gap-2 border-b px-2 py-1.5 sticky top-0 bg-popover">
            <Search className="size-3.5 text-muted-foreground shrink-0" />
            <Input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Tìm..."
              className="h-7 border-0 shadow-none focus-visible:ring-0 px-0 text-xs"
            />
            {loading ? (
              <Loader2 className="size-3.5 text-muted-foreground animate-spin shrink-0" />
            ) : null}
          </div>

          {error ? (
            <div className="px-3 py-3 text-xs text-destructive">{error}</div>
          ) : items.length === 0 ? (
            <div className="px-3 py-3 text-xs text-muted-foreground">
              {loading
                ? "Đang tải..."
                : allowFreeText && query.trim()
                  ? `Enter để dùng "${query.trim()}"`
                  : "Không có kết quả."}
            </div>
          ) : (
            items.map((it, i) => (
              <button
                key={it.id}
                type="button"
                onClick={() => pick(it)}
                onMouseEnter={() => setActiveIdx(i)}
                className={cn(
                  "w-full flex items-start gap-2 px-2.5 py-1.5 text-left text-xs transition-colors",
                  i === activeIdx ? "bg-primary/10" : "hover:bg-muted/50",
                )}
              >
                <span
                  className={cn(
                    "size-3.5 mt-0.5 shrink-0",
                    value === it.id ? "text-primary" : "text-transparent",
                  )}
                >
                  <Check className="size-3.5" />
                </span>
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{it.label}</div>
                  {it.secondary ? (
                    <div className="text-[10px] text-muted-foreground truncate">
                      {it.secondary}
                    </div>
                  ) : null}
                </div>
                {it.tags && it.tags.length > 0 ? (
                  <div className="flex gap-0.5 shrink-0">
                    {it.tags.slice(0, 2).map((t) => (
                      <span
                        key={t}
                        className="text-[9px] px-1 rounded bg-muted text-muted-foreground"
                      >
                        {t}
                      </span>
                    ))}
                  </div>
                ) : null}
              </button>
            ))
          )}
        </div>
      ) : null}

      {hint && !open ? (
        <p className="text-[10px] text-muted-foreground mt-0.5">{hint}</p>
      ) : null}
    </div>
  );
}
